import React, {useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import {NotificationTypes, NotificationLevels} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import Text from '@adsk/bim360-matrix-react-text';
import TemplateEditModalContainer from './TemplateEditModalContainer';
import {templateType} from '../../constants/proptype-validation';
import {parseTemplateToPendingTemplateForm} from '../../utils/issue-template-helpers';
import {TEMPLATE_FIELD_NAMES, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES} from '../../constants/issue-templates';

const useFetchTemplateIfMissing = (previewTemplateId, isPreviewTemplateFetched, fetchIssueTemplateInstanceAction) => {
  useEffect(() => {
    if (previewTemplateId && !isPreviewTemplateFetched) {
      fetchIssueTemplateInstanceAction(previewTemplateId);
    }
  }, [previewTemplateId, isPreviewTemplateFetched, fetchIssueTemplateInstanceAction]);
};

const useLoadTemplateValuesToForm = (previewTemplate, loadTemplateToForm) => {
  useEffect(() => {
    if (previewTemplate) {
      const formValuesFromTemplate = parseTemplateToPendingTemplateForm(previewTemplate);
      loadTemplateToForm('pendingTemplate', formValuesFromTemplate);
    }
  }, [previewTemplate, loadTemplateToForm]);
};

const TemplatePreviewModal = ({
  previewTemplateId,
  previewTemplate,
  fetchIssueTemplateInstanceAction,
  showNotificationAction,
  container,
  onClose,
  loadTemplateToForm
}) => {
  useFetchTemplateIfMissing(previewTemplateId, !!previewTemplate, fetchIssueTemplateInstanceAction);
  useLoadTemplateValuesToForm(previewTemplate, loadTemplateToForm);

  const onSaveSuccess = useCallback(template => {
    showNotificationAction({
      type: NotificationTypes.TOAST,
      level: NotificationLevels.SUCCESS,
      message: (
        <Text hasHTML>
          {i18n.t('templatesGrid.previewModal.updatedMessage', {
            title: template[TEMPLATE_FIELD_NAMES.ISSUE_ATTRIBUTES][TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE]
          })}
        </Text>
      )
    });
  }, [showNotificationAction]);

  const onSaveError = useCallback(() => {
    showNotificationAction({
      type: NotificationTypes.SNACKBAR,
      level: NotificationLevels.ERROR,
      message: i18n.t('templatesGrid.previewModal.errorMessage')
    });
  }, [showNotificationAction]);

  return (
    <TemplateEditModalContainer
      isOpen={!!previewTemplateId}
      templateId={previewTemplateId}
      title={i18n.t('templatesGrid.previewModal.title')}
      saveButtonText={i18n.t('navigation.save')}
      savingButtonText={i18n.t('navigation.saving')}
      container={container}
      onClose={onClose}
      onSaveSuccess={onSaveSuccess}
      onSaveError={onSaveError}
    />
  );
};

TemplatePreviewModal.displayName = 'TemplatePreviewModal';

TemplatePreviewModal.propTypes = {
  previewTemplateId: PropTypes.string,
  previewTemplate: templateType,
  pendingTemplateForm: PropTypes.object.isRequired,
  container: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  showNotificationAction: PropTypes.func.isRequired,
  fetchIssueTemplateInstanceAction: PropTypes.func.isRequired,
  loadTemplateToForm: PropTypes.func.isRequired
};

export default TemplatePreviewModal;
